import { isRequestError } from 'utils/requestError';
import { InputPinCode, InputText, Loading, PopupWraper } from 'components';
import { Button, InputWraper, useForm } from 'modules';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useCheckPinCodeMutation } from 'services';
import { WithdrawRequestService } from 'services/openapi';
import * as Yup from 'yup';

interface ApproveBidvAutoRequestWithdrawProps {
  onClose: () => void;
  forceUpdateTable: () => void;
  ids: string[];
}

const ApproveBidvAutoRequestWithdraw = ({
  onClose,
  forceUpdateTable,
  ids,
}: ApproveBidvAutoRequestWithdrawProps) => {
  const [approveErrors, setApproveErrors] = useState<string[]>([]);
  const { mutateAsync: mutateApproveAsync, isLoading: isLoadingApprove } =
    useMutation({
      mutationFn: (request: {
        withdrawRequestIds: string[];
        pinCode: string;
        remark?: string;
      }) =>
        WithdrawRequestService.withdrawRequestControllerApproveBidvAutoBatch(
          request as any,
        ),
    });

  const { mutateAsync: mutateCheckPin, isLoading: isLoadingPin } =
    useCheckPinCodeMutation();

  const { handleSubmit, getInputProps } = useForm({
    structure: [
      {
        name: 'remark',
        label: 'Nội dung chuyển khoản',
        defaultValue: '',
        validate: Yup.string().required('Vui lòng nhập nội dung chuyển khoản'),
      },
      {
        name: 'pinCode',
        label: 'Mật khẩu',
        defaultValue: '',
        validate: Yup.string()
          .required('Vui lòng nhập mật khẩu')
          // .matches(/^\d+$/, 'Mật khẩu phải là số')
          .test(
            'len',
            'Mật khẩu phải có ít nhất 6 kí tự',
            (val) => (val?.toString().length ?? 0) >= 6,
          ),
      },
    ],
    onSubmit: async (values) => {
      try {
        setApproveErrors([]);

        if (!ids.length) {
          toast.error('Vui lòng chọn yêu cầu cần duyệt');
          return;
        }

        const pinCode = String(values.pinCode);
        const checkResult = await mutateCheckPin({ pinCode });
        if (!checkResult?.isValid) {
          toast.error('Mật khẩu không chính xác');
          return;
        }

        const request = {
          withdrawRequestIds: ids,
          remark: values.remark,
          pinCode,
        };
        const result = await mutateApproveAsync(request);
        const resultItems = result?.data || [];
        const successCount = resultItems.length
          ? resultItems.filter((item) => item.success).length
          : ids.length;
        const failedItems = resultItems.filter((item) => !item.success);
        const failedCount = failedItems.length;
        const failedMessages = failedItems.map((item) => {
          return `${item.withdrawRequestId}: ${
            item.error || 'Duyệt chuyển tiền BIDV thất bại'
          }`;
        });

        forceUpdateTable();

        if (failedCount) {
          setApproveErrors(failedMessages);

          if (successCount) {
            toast.success(
              `Duyệt với BIDV thành công ${successCount}/${ids.length} yêu cầu`,
            );
            toast.error(
              `Có ${failedCount}/${ids.length} yêu cầu lỗi: ${
                failedItems[0]?.error || 'Duyệt chuyển tiền BIDV thất bại'
              }`,
              { duration: 6000 },
            );
            onClose();
            return;
          }

          toast.error(
            failedItems[0]?.error ||
              'Duyệt chuyển tiền BIDV thất bại, vui lòng kiểm tra lại',
            { duration: 6000 },
          );
          return;
        }

        onClose();
        toast.success(
          `Duyệt với BIDV thành công ${successCount}/${ids.length} yêu cầu`,
        );
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Có lỗi xảy ra, vui lòng thử lại');
        }
        console.error(error);
      }
    },
  });

  return (
    <div className='wrap-pop-up-create-user bidv-auto-approve-popup'>
      <PopupWraper
        center
        title='Duyệt với BIDV'
        onClose={onClose}
        style={{ maxWidth: '380px', width: 'calc(100vw - 32px)' }}
      >
        <Loading isLoading={isLoadingPin || isLoadingApprove}>
          <div className='container-fluid'>
            <div className='bidv-auto-approve-popup__warning'>
              Khi duyệt sẽ phát sinh giao dịch Chuyển tiền tự động từ Tài khoản
              Chi.
            </div>
            <div className='bidv-auto-approve-popup__warning'>
              Số yêu cầu duyệt: <strong>{ids.length}</strong>
            </div>
            {approveErrors.length > 0 && (
              <div className='bidv-auto-approve-popup__errors'>
                {approveErrors.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            )}
            <div className='wrap-form-accept-request'>
              <InputWraper
                className='col-lg-12'
                inputProps={getInputProps('remark')}
                component={InputText}
              />
              <InputWraper
                className='col-lg-12'
                inputProps={getInputProps('pinCode')}
                component={InputPinCode}
              />
            </div>
          </div>
          <div className='wrap-button-create-user'>
            <Button
              label='Xác nhận'
              type='submit'
              onClick={handleSubmit}
              buttonType='success'
              isLoading={isLoadingPin || isLoadingApprove}
            />
            <Button label='Trở về' buttonType='grey' onClick={onClose} />
          </div>
        </Loading>
      </PopupWraper>
    </div>
  );
};

export default ApproveBidvAutoRequestWithdraw;
